/**
 * Admin view of one member's payment plan, opened from the members table.
 * Read-only by default; the manual charge / force-failure buttons only appear when
 * MEMBERSHIP_TEST_TOOLS is switched on in the backend environment.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';
import { API } from '@/App';

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const day = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const statusStyle = {
  paid: 'bg-green-100 text-green-800',
  scheduled: 'bg-gray-100 text-gray-700',
  failed: 'bg-red-100 text-red-800',
};

export const MemberPlanModal = ({ userId, memberName, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/membership/plan/${userId}`, auth());
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load plan');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const act = async (path, label) => {
    setBusy(path);
    try {
      const res = await axios.post(`${API}/admin/membership/plan/${userId}/${path}`, {}, auth());
      toast.success(res.data?.ok === false ? `${label}: ${res.data.error}` : `${label} done`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || `${label} failed`);
    } finally {
      setBusy('');
    }
  };

  const plan = data?.plan;
  const tools = data?.test_tools_enabled;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="member-plan-modal">
        <DialogHeader>
          <DialogTitle>Membership Plan — {memberName}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : !data ? (
          <p className="text-sm text-gray-500">Nothing to show.</p>
        ) : (
          <div className="space-y-5 text-sm">
            {data.is_frozen && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="plan-frozen-banner">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">On Bronze after a failed payment</p>
                  <p className="text-red-700">
                    Real tier: <strong className="capitalize">{data.member?.frozen_from_tier}</strong> ·{' '}
                    {data.frozen_reason}
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Tier" value={<span className="capitalize">{data.tier}</span>} />
              <Stat label="Plan" value={plan ? `${plan.payments_made} of ${plan.plan_count}` : 'None'} />
              <Stat label="Base paid" value={money(data.base_paid_on_current_tier)} />
              <Stat label="Upgrade credit" value={money(data.credit_toward_upgrade)} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Next payment" value={plan?.next_due_date ? day(plan.next_due_date) : '—'} />
              <Stat label="Maintenance" value={money(data.maintenance?.amount)} />
              <Stat label="Anniversary" value={day(data.maintenance?.next_due)} />
              <Stat label="Card on file" value={data.card
                ? `${data.card.brand} ••${data.card.last4} · ${String(data.card.exp_month).padStart(2, '0')}/${data.card.exp_year}`
                : (data.saved_card_on_file ? 'Yes' : 'No')} />
            </div>

            {data.card && (data.card.expired || data.card.expires_before_next_payment) && (
              <p className="text-xs text-red-700" data-testid="admin-card-expiry-warning">
                Their card {data.card.expired ? 'has expired' : 'expires before the next payment'} — they've
                been asked to update it.
              </p>
            )}

            {plan?.schedule?.length > 0 && (
              <div>
                <p className="font-semibold mb-2">Payment schedule</p>
                <div className="border rounded-lg divide-y">
                  {plan.schedule.map((entry) => (
                    <div key={entry.number} className="flex items-center justify-between px-3 py-2"
                         data-testid={`plan-row-${entry.number}`}>
                      <span className="text-gray-600">#{entry.number} · {day(entry.due_date)}</span>
                      <span className="flex items-center gap-3">
                        <span className="font-medium">{money(entry.billed)}</span>
                        <Badge className={statusStyle[entry.status] || 'bg-gray-100 text-gray-700'}>
                          {entry.status}{entry.attempts > 1 ? ` · ${entry.attempts} tries` : ''}
                        </Badge>
                      </span>
                    </div>
                  ))}
                </div>
                {plan.schedule.some((e) => e.last_error) && (
                  <p className="text-xs text-red-700 mt-2">
                    Last decline: {plan.schedule.filter((e) => e.last_error).slice(-1)[0].last_error}
                  </p>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {data.is_frozen && (
                <Button size="sm" onClick={() => act('reinstate', 'Reinstate')}
                        disabled={busy === 'reinstate'} data-testid="plan-reinstate-btn">
                  Reinstate access
                </Button>
              )}
              {tools ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => act('charge-next', 'Charge next payment')}
                          disabled={!plan || busy === 'charge-next'} data-testid="plan-charge-next-btn">
                    Charge next payment now
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => act('force-failure', 'Force a failure')}
                          disabled={!plan || busy === 'force-failure'} data-testid="plan-force-failure-btn">
                    Force a failed payment
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => act('charge-maintenance', 'Charge maintenance')}
                          disabled={busy === 'charge-maintenance'} data-testid="plan-charge-maintenance-btn">
                    Charge maintenance now
                  </Button>
                </>
              ) : (
                <p className="text-xs text-gray-500 self-center" data-testid="plan-tools-off">
                  Test tools are off in this environment.
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-lg bg-gray-50 border p-2">
    <span className="block text-xs text-gray-500">{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);

export default MemberPlanModal;
